import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FaBars, FaRegUserCircle, FaTimes } from "react-icons/fa";
import logo from "../assets/logo.png";
import { CiShoppingCart, CiUser } from "react-icons/ci";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion"; // Added for smooth modal

const Navbar = () => {
  const [toggle, setToggle] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false); // Modal State
  const handleToggle = () => setToggle(!toggle);
  const [activeTab, setActiveTab] = useState("home");
  const location = useLocation();
  const pathname = location.pathname;
  const [isVisible, setIsVisible] = useState(false);
  const { user, signOut } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      // no-op
    }
  };

  const openBooking = (url) => {
    window.open(url, "_blank");
    setIsBookingModalOpen(false);
  };

  useEffect(() => {
    if (pathname === "/") setActiveTab("home");
    else if (pathname === "/academy") setActiveTab("academy");
    else if (pathname === "/about-us") setActiveTab("about-us");
    else if (pathname === "/contact") setActiveTab("contact");
    else setActiveTab("");
  }, [pathname]);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Container className={isVisible ? "visible" : ""}>
        <div className="logo-container">
          <Link to={"/"} className="link-styles">
            <img src={logo} alt="Logo" />
          </Link>
        </div>

        <ul className="nav-menu-center">
          <Link to={"/"} className="link-styles">
            <li className={activeTab === "home" ? "activeTab" : "nonActive"}>
              Home
            </li>
          </Link>

          {/* TRIGGER MODAL INSTEAD OF LINK */}
          <li
            className={activeTab === "booking" ? "activeTab" : "nonActive"}
            onClick={() => setIsBookingModalOpen(true)}
          >
            Booking
          </li>

          <Link to={"/academy"} className="link-styles">
            <li className={activeTab === "academy" ? "activeTab" : "nonActive"}>
              Academy
            </li>
          </Link>
          <Link to={"/about-us"} className="link-styles">
            <li
              className={activeTab === "about-us" ? "activeTab" : "nonActive"}
            >
              About Us
            </li>
          </Link>
          <Link to={"/contact"} className="link-styles">
            <li className={activeTab === "contact" ? "activeTab" : "nonActive"}>
              Contact Us
            </li>
          </Link>
        </ul>

        <div className="nav-buttons">
          {user ? (
            <>
              <UserPill title={user.email}>{user.email || "Account"}</UserPill>
              <button className="login" onClick={handleLogout}>
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link to={"/login"} className="link-styles">
                <button className="login">Log In</button>
              </Link>
              <Link to={"/signup"} className="link-styles">
                <button>Sign Up</button>
              </Link>
            </>
          )}
        </div>

        <div className="mobile-menu" onClick={handleToggle}>
          {toggle ? <FaTimes className="icon" /> : <FaBars className="icon" />}
        </div>

        {/* Mobile menu */}
        <ul className={toggle ? "nav-menu-mobile active" : "nav-menu-mobile"}>
          <Link to={"/"} onClick={handleToggle} className="link-styles">
            <li>Home</li>
          </Link>
          <li
            onClick={() => {
              setIsBookingModalOpen(true);
              handleToggle();
            }}
          >
            Booking
          </li>
          <Link to={"/academy"} onClick={handleToggle} className="link-styles">
            <li>Academy</li>
          </Link>
          <Link to={"/about-us"} onClick={handleToggle} className="link-styles">
            <li>About Us</li>
          </Link>
          <Link to={"/contact"} onClick={handleToggle} className="link-styles">
            <li>Contact Us</li>
          </Link>

          <div className="buttons">
            {user ? (
              <button className="login" onClick={handleLogout}>
                Log Out
              </button>
            ) : (
              <Link to={"/login"} className="link-styles">
                <button className="login">Log In</button>
              </Link>
            )}
          </div>
        </ul>
      </Container>

      {/* --- BOOKING SELECTION MODAL --- */}
      <AnimatePresence>
        {isBookingModalOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsBookingModalOpen(false)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setIsBookingModalOpen(false)}>
                &times;
              </CloseBtn>
              <h3>Termin buchen</h3>
              <p>Wählen Sie Ihre bevorzugte Buchungsplattform:</p>

              <ButtonGroup>
                <PlatformBtn
                  onClick={() =>
                    openBooking(
                      "https://buchung.treatwell.de/ort/era-aesthetics-academy/",
                    )
                  }
                >
                  <span>Treatwell</span>
                  <small>Schnelle Online-Buchung</small>
                </PlatformBtn>

                <PlatformBtn
                  onClick={() =>
                    openBooking(
                      "https://beautinda.de/artist/glAjRxw9FkUTkPh98ZNpnvDTdqV2",
                    )
                  }
                >
                  <span>Beautinda</span>
                  <small>Alternative Buchung</small>
                </PlatformBtn>
              </ButtonGroup>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

// --- MODAL STYLES ---

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(109, 99, 87, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: #fdfcfb;
  padding: 2.5rem;
  border-radius: 30px;
  max-width: 450px;
  width: 100%;
  text-align: center;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid #e9e3d0;

  h3 {
    font-family: "Playfair Display", serif;
    font-size: 1.8rem;
    color: #5a5248;
    margin-bottom: 0.5rem;
  }
  p {
    color: #6d6357;
    margin-bottom: 2rem;
    font-size: 0.95rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PlatformBtn = styled.button`
  background: #fdfcfb;
  border: 1px solid rgba(109, 99, 87, 0.2);
  padding: 16px;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;

  span {
    display: block;
    font-size: 1.1rem;
    font-weight: 600;
    color: #5a5248;
  }
  small {
    color: #857869;
    font-size: 0.75rem;
    text-transform: uppercase;
  }

  &:hover {
    background: #6d6357;
    span,
    small {
      color: white;
    }
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #5a5248;
  cursor: pointer;
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
`;
const Container = styled.div`
  -webkit-backdrop-filter: blur(7px);
  backdrop-filter: blur(7px);
  background-color: #cfc0afad;
  margin: 2em 7%;
  position: fixed;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1% 4%;
  width: 80%;
  margin: auto;
  height: 60px;
  z-index: 100;
  border-radius: 30px;
  box-shadow: 0 0 11px 2px #e2e1e2ce;
  box-sizing: border-box;
  border: 2px solid #e9e3d0;
  @media (max-width: 750px) {
    margin: 1em 5%;
    width: 90%;
  }

  /* Smooth animation on load */
  opacity: 0;
  transform: translateY(-20px);
  transition:
    opacity 0.5s ease-out,
    transform 0.5s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .logo-container {
    flex: 1;
    display: flex;
    align-items: center;

    img {
      height: 40px;
      margin-top: 5px;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .nav-menu-center {
    flex: 2;
    display: flex;
    justify-content: center;
    gap: 37px;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      color: #6d6357;
      font-weight: 400;
      transition: all 0.3s ease;
      cursor: pointer;
      padding: 8px 16px;
      border-radius: 20px;

      &:hover {
        color: #6d6357;
        background-color: rgba(109, 99, 87, 0.1);
        transform: translateY(-2px);
      }
    }

    .activeTab {
      text-decoration: underline;
      text-underline-offset: 7px;
      font-weight: 600;
      /* background-color: rgba(109, 99, 87, 0.15); */
    }

    .link-styles {
      text-decoration: none;
    }
  }

  .nav-buttons {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;

    .login {
      background: none;
      color: #6d6357;
      /* border: 2px solid #6d6357; */
    }

    button {
      border: none;
      background: #6d6357;
      padding: 8px 24px;
      color: white;
      font-size: 16px;
      border-radius: 20px;
      font-family: "Poppins", cursive;
      cursor: pointer;
      transition: all 0.4s ease;

      &:hover {
        box-shadow: 0 0 14px 2px rgba(109, 99, 87, 0.3);
        transform: translateY(-2px);
      }
    }
  }

  /* Mobile menu button */
  .mobile-menu {
    position: absolute;
    color: #6d6357;
    right: 5%;
    font-size: 24px;
    cursor: pointer;
    transition: transform 0.3s ease;
    display: none;
    margin-top: 7px;

    &:hover {
      transform: scale(1.1);
    }
  }

  /* Mobile navigation menu */
  .nav-menu-mobile {
    background: #f2efe4;
    width: 90%;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding: 10%;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 0%;
    right: -120%;
    transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 99;
    box-shadow: 0 0 12px 2px rgba(128, 128, 128, 0.2);
    list-style: none;
    margin: 0;

    li {
      color: #6d6357;
      font-weight: 400;
      transition: all 0.3s ease;
      cursor: pointer;
      padding: 15px 30px;
      margin: 10px 0;
      width: 100%;
      text-align: center;
      border-radius: 25px;

      &:hover {
        background-color: rgba(109, 99, 87, 0.1);
        transform: scale(1.05);
      }
    }

    .activeTab {
      text-decoration: underline;
      text-underline-offset: 7px;
      font-weight: 600;
      background-color: rgba(109, 99, 87, 0.15);
    }

    .buttons {
      margin-top: 30px;
      display: flex;
      flex-direction: column;
      gap: 15px;
      width: 100%;

      .login {
        background: none;
        color: #6d6357;
        border: 2px solid #6d6357;
      }

      button {
        border: none;
        background: #6d6357;
        padding: 12px 30px;
        color: white;
        font-size: 16px;
        border-radius: 25px;
        font-family: "Poppins", cursive;
        cursor: pointer;
        transition: all 0.4s ease;
        width: 100%;

        &:hover {
          box-shadow: 0 0 14px 2px rgba(109, 99, 87, 0.3);
          transform: translateY(-2px);
        }
      }
    }

    &.active {
      right: -10%;
      top: 0;
    }

    .link-styles {
      text-decoration: none;
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }

  @media (max-width: 1150px) {
    .nav-menu-center,
    .nav-buttons {
      display: none;
    }

    .mobile-menu {
      display: block;
      z-index: 100;
    }

    .logo-container {
      flex: none;
    }
  }

  @media (min-width: 751px) {
    margin: 1em 10%;
    .nav-menu-mobile {
      display: none;
    }
  }

  @media (max-width: 470px) {
    img {
      max-height: 34px;
      margin-left: 15%;
    }
    .nav-menu-mobile {
      width: 97%;
    }
    .mobile-menu {
      right: 10%;
    }
  }

  @media (max-width: 370px) {
    .nav-menu-mobile {
      width: 98%;
    }
  }
`;

const UserPill = styled.div`
  max-width: 240px;
  padding: 8px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(109, 99, 87, 0.2);
  color: #6d6357;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default Navbar;
